import { Button, Input, Label } from '@lensora/ui'
import * as React from 'react'

import { SuggestionChips } from '../SuggestionChips'
import type { PromptsErrorKind } from '../useOnboardingMachine'

interface PromptsTurnProps {
  resolved: boolean
  summary?: string | undefined
  prompts: string[]
  usedAi: boolean
  loading: boolean
  errorKind: PromptsErrorKind | null
  saving: boolean
  onRetry: () => void
  onSubmit: (selected: string) => void
  onSkip: () => void
  // Lifted to OnboardingChat so the persistent composer bar's "add custom
  // prompt" action shares the exact same selection state as this turn's
  // inline "Add your own" input, rather than forking the logic.
  selected: string | null
  onToggle: (prompt: string) => void
  onAddCustom: (prompt: string) => void
}

function introCopy(loading: boolean, errorKind: PromptsErrorKind | null, usedAi: boolean, hasPrompts: boolean): string {
  if (loading) return 'Analysing your website — this can take up to 15 seconds…'
  if (errorKind === 'insufficient_credits') {
    return "You're out of credits, so I couldn't generate suggestions — you can continue without them and add prompts manually later."
  }
  if (errorKind === 'generic') {
    return "Something went wrong generating suggestions — you can retry, or add prompts manually later."
  }
  if (!hasPrompts) return 'No suggestions generated — add prompts manually from the Prompts page.'
  if (!usedAi) return "Here are some starter queries — pick one to preview live, the rest will be waiting for you to track."
  return "These are the queries your customers search. Pick one to preview live — the rest will be waiting in Suggested on the Prompts page."
}

export function PromptsTurn({
  resolved, summary, prompts, usedAi, loading, errorKind, saving, onRetry, onSubmit, onSkip, selected, onToggle, onAddCustom,
}: PromptsTurnProps) {
  const [customPrompt, setCustomPrompt] = React.useState('')

  if (resolved) {
    return <p className="text-sm text-text-muted">{summary}</p>
  }

  function addCustom() {
    if (!customPrompt.trim()) return
    onAddCustom(customPrompt.trim())
    setCustomPrompt('')
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-muted">{introCopy(loading, errorKind, usedAi, prompts.length > 0)}</p>

      {errorKind === 'generic' && (
        <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>
      )}

      {(prompts.length > 0 || selected !== null) && (
        <SuggestionChips
          options={Array.from(new Set(selected ? [...prompts, selected] : prompts))}
          selected={(p) => p === selected}
          onToggle={onToggle}
          disabled={saving}
        />
      )}

      <div className="space-y-1.5">
        <Label htmlFor="onboarding-custom-prompt" className="text-[11px] font-semibold uppercase tracking-wider">
          Add your own
        </Label>
        <div className="flex gap-2">
          <Input
            id="onboarding-custom-prompt"
            placeholder="e.g. best accounting software for freelancers"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (customPrompt.trim()) addCustom()
                else if (!saving && selected) onSubmit(selected)
              }
            }}
          />
          {customPrompt.trim() && (
            <Button type="button" size="sm" variant="outline" onClick={addCustom}>+ Add</Button>
          )}
        </div>
      </div>

      <div className="flex gap-2.5">
        <Button size="sm" disabled={saving || !selected} onClick={() => selected && onSubmit(selected)}>
          {saving ? 'Finishing…' : 'Continue →'}
        </Button>
        <button type="button" onClick={onSkip} className="text-sm text-text-muted hover:text-text">
          Skip for now
        </button>
      </div>
    </div>
  )
}
